import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { twJoin, twMerge } from 'tailwind-merge';
import { Tooltip } from '../Tooltip/Tooltip';

interface TabItem {
  id: string;
  name: string;
  hidden?: boolean;
  disabled?: boolean;
  tooltip?: string;
}

type TabIds<T extends readonly TabItem[]> = T[number]['id'];
type RenderTabProps<T extends readonly TabItem[]> = {
  tab: T[number];
  activeTab: T[number];
  props: { role: string; 'data-tab-index': number; 'aria-selected'?: boolean; disabled?: boolean };
  onClick: () => void;
};
interface SlidingTabsProps<T extends readonly TabItem[]> {
  tabs: T;
  defaultActiveTab?: TabIds<T>;
  containerClass?: string;
  vertical?: boolean;
  onChange?: (tabId: TabIds<T>) => void;
  renderTab?: (props: RenderTabProps<T>) => React.ReactNode;
  renderActiveBg?: (props: { activeTab: T[number]; style: React.CSSProperties }) => React.ReactNode;
}

export const SlidingTabs = <T extends readonly TabItem[]>({
  tabs,
  defaultActiveTab,
  containerClass,
  vertical,
  onChange,
  renderTab,
  renderActiveBg,
}: SlidingTabsProps<T>) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [activeTabIndex, setActiveTabIndex] = useState<number>(() => {
    const index = tabs.findIndex((tab) => tab.id === defaultActiveTab);
    return index >= 0 ? index : 0;
  });

  const indexOfDefaultTab = tabs.findIndex((tab) => tab.id === defaultActiveTab);

  useEffect(() => {
    setActiveTabIndex(indexOfDefaultTab);
  }, [indexOfDefaultTab]);

  const [backgroundStyle, setBackgroundStyle] = useState<React.CSSProperties>({
    position: 'absolute',
    left: 2,
  });

  const updateBackgroundStyle = useCallback(() => {
    // Wait for next frame. This ensures button and container widths are correct.
    requestAnimationFrame(() => {
      const activeButton = containerRef.current?.querySelector(
        `[data-tab-index="${activeTabIndex}"]`
      );

      if (activeButton && containerRef.current) {
        const containerRect = containerRef.current.getBoundingClientRect();
        const buttonRect = activeButton.getBoundingClientRect();

        if (vertical) {
          setBackgroundStyle({
            position: 'absolute',
            top: `${buttonRect.top - containerRect.top}px`,
            left: 0,
            width: `${buttonRect.width}px`,
            height: `${buttonRect.height}px`,
            zIndex: 10,
            transition: 'top 0.3s ease, height 0.3s ease',
          });
        } else {
          setBackgroundStyle({
            position: 'absolute',
            left: `${buttonRect.left - containerRect.left}px`,
            width: `${buttonRect.width}px`,
            height: `${buttonRect.height}px`,
            zIndex: 10,
            transition: 'left 0.3s ease, width 0.3s ease',
          });
        }
      }
    });
  }, [activeTabIndex, vertical]);

  const filteredTabs = tabs.filter((tab) => !tab.hidden);

  useLayoutEffect(() => {
    updateBackgroundStyle();

    const resizeObserver = new ResizeObserver(updateBackgroundStyle);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }
    return () => {
      resizeObserver.disconnect();
    };
    // Include `tabs` so that background style is updated if tabs change.
  }, [updateBackgroundStyle, tabs]);

  const handleTabClick = (index: number) => {
    const tab = filteredTabs[index];
    if (tab && !tab.disabled) {
      setActiveTabIndex(index);
      onChange?.(tab.id);
    }
  };

  const defaultRenderTab = ({ tab, activeTab, onClick, props }: RenderTabProps<T>) => (
    <button
      {...props}
      className={twJoin(
        // Horizontal spacing defaults to px-4, growing uniformly if a width is
        // set on SlidingTabs.
        'flex-1 grow text-nowrap px-4 py-3 text-sm font-semibold outline-none transition-colors duration-200',
        tab.disabled
          ? 'cursor-not-allowed text-contentTertiary'
          : tab.id === activeTab.id
            ? 'text-contentPrimary'
            : 'relative text-contentSecondary hover:text-contentPrimary focus-visible:after:absolute focus-visible:after:inset-x-0 focus-visible:after:-bottom-px focus-visible:after:h-0.5 focus-visible:after:bg-contentBlue focus-visible:after:opacity-50'
      )}
      onClick={onClick}
      disabled={tab.disabled}
    >
      {tab.tooltip ? <Tooltip content={tab.tooltip}>{tab.name}</Tooltip> : tab.name}
    </button>
  );

  const activeTab = tabs[activeTabIndex];
  if (!activeTab) {
    // This should never happen, helping ts.
    return null;
  }

  return (
    <div
      ref={containerRef}
      className={twMerge('relative flex w-fit', vertical ? 'flex-col' : '', containerClass)}
    >
      {renderActiveBg ? (
        renderActiveBg({
          activeTab,
          style: backgroundStyle,
        })
      ) : (
        <div
          className="bg-contentBlue"
          style={{
            ...backgroundStyle,
            ...(vertical
              ? {
                  width: '2px',
                }
              : {
                  height: '2px',
                  // Hang over by 1px
                  bottom: -1,
                }),
          }}
        />
      )}
      {filteredTabs.map((tab, index) => (
        <React.Fragment key={tab.id}>
          {(renderTab || defaultRenderTab)({
            tab,
            activeTab,
            props: {
              role: 'tab',
              'data-tab-index': index,
              'aria-selected': index === activeTabIndex,
              disabled: tab.disabled,
            },
            onClick: () => handleTabClick(index),
          })}
        </React.Fragment>
      ))}
    </div>
  );
};
